import { logException } from '@atlaskit/editor-common/monitoring/error';

export const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';

export interface SnippetMediaToken {
	baseUrl: string;
	clientId: string;
	collectionName: string;
	token: string;
}

/**
 * Raw response shape returned by the Block Service media-token endpoint.
 * Maps to the OpenAPI SnippetMediaTokenResponse schema.
 */
interface SnippetMediaTokenResponse {
	collectionNames: Record<string, string>;
	config: {
		clientId: string;
		fileStoreUrl: string;
		maxFileSize?: number | null;
	};
	expirationTime: number;
	token: string;
}

/**
 * Fetches a media token for a specific snippet's media collection.
 * This token allows reading media files that belong to the snippet.
 */
export const fetchSnippetMediaToken = async (snippetId: string): Promise<SnippetMediaToken> => {
	const response = await fetch(
		`${BLOCK_SERVICE_API_URL}/snippets/${encodeURIComponent(snippetId)}/media-token`,
		{ method: 'GET' },
	);

	if (!response.ok) {
		const error = new Error(`Failed to fetch snippet media token for snippet ${snippetId}`);
		logException(error, {
			location: 'editor-snippets-provider/fetchSnippetMediaToken',
		});
		throw error;
	}

	const data: SnippetMediaTokenResponse = await response.json();

	return {
		clientId: data.config.clientId,
		token: data.token,
		baseUrl: data.config.fileStoreUrl,
		collectionName: data.collectionNames?.[snippetId] ?? `snippet-${snippetId}`,
	};
};

/**
 * Parses a snippet contextId string (JSON format) and extracts the snippetId.
 * The contextId format is: {"snippetId": "...", "scope": "..."}
 * Returns undefined if parsing fails or snippetId is missing.
 */
export const parseSnippetContextId = (
	contextId: string,
): { scope?: string; snippetId: string } | undefined => {
	try {
		const { snippetId, scope } = JSON.parse(contextId);
		return typeof snippetId === 'string' && snippetId ? { snippetId, scope } : undefined;
	} catch {
		logException(new Error('Failed to parse snippet context id'), {
			location: 'editor-snippets-provider/parseSnippetContextId',
		});
		return undefined;
	}
};
